import Image from "next/image";
import { TelephoneTextField, TextFieldSts } from "staysure-component-library";

import { useContactDetailsContext } from "@/app/components/contact-details/provider/contact-details.provider";
import { ContactNumberSectionSectionProps } from "@/app/components/contact-details/types/contact-details.type";
import { formCmsData } from "@/app/components/contact-details/utils/contact-details-page.util";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import RichText from "@/components/atoms/rich-text/rich-text";
import { PRODUCT } from "@/constants";
import { handleFocusScrollContactForm } from "@/utils/scrollUtils";

function ContactNumberSection({
  brand = PRODUCT as "staysure" | "avanti",
  phoneRef,
  invalidPhoneMessage,
}: Readonly<ContactNumberSectionSectionProps>) {
  const { cmsData, handlers, formValues, validations, setters, flags } =
    useContactDetailsContext();
  const { mobileDescription, mobileLabel } = formCmsData(cmsData);
  const {
    handleMobileNumberChange,
    handleContinue,
    handleBlur,
    handleOptionSelect,
  } = handlers;
  const { phone, telCountryCode, dropDownListToDisplay } = formValues;
  const { isPhoneNumberValid } = validations;
  const { setInvalidPhoneMessage } = setters;
  const { isTablet } = flags;
  const isTabletDevice = Boolean(isTablet?.[0]);

  const { current } = useWizard();
  const isUseSimilarWidthForTelAndEmail =
    current?.uiElementControls?.alignTelephoneFieldWidthWithEmail;

  return (
    <>
      <div className="mt-spacing-l">
        {mobileDescription && (
          <RichText
            richText={mobileDescription}
            className=" text-[14px] font-body-font break-words leading-[21px] text-400 text-text-body contact-rich-text"
          />
        )}
        <button
          type="button"
          data-testid="contact-phone-btn"
          tabIndex={-1}
          ref={phoneRef}
          onFocus={() =>
            handleFocusScrollContactForm(
              "phoneValue",
              phoneRef as React.MutableRefObject<HTMLButtonElement | null>
            )
          }
          className={`mt-spacing-s lg:mt-spacing-xs ${
            isUseSimilarWidthForTelAndEmail ? "md:w-[347px]" : "md:w-[225px]"
          } w-full`}
        >
          {current?.uiElementControls?.showTelephoneTextfield && (
            <TextFieldSts
              label={mobileLabel}
              id="contact-mobile"
              data-testid="contact-mobile-input"
              width="100%"
              state={invalidPhoneMessage !== "" ? "error" : ""}
              message={invalidPhoneMessage ?? ""}
              onChange={(e) => handleMobileNumberChange(e)}
              value={phone}
              type="tel"
              onBlur={(event) => {
                if (
                  event.relatedTarget &&
                  event.relatedTarget.id === "actionButton2"
                ) {
                  handleContinue();
                } else {
                  handleBlur(isPhoneNumberValid, phone, setInvalidPhoneMessage);
                }
              }}
              brand={brand}
            />
          )}
          {current?.uiElementControls?.showTelephoneCountryCode && (
            <button
              type="button"
              data-testid="contact-phone-btn"
              ref={phoneRef}
              onFocus={() => {
                handleFocusScrollContactForm(
                  "phoneValue",
                  phoneRef as React.MutableRefObject<HTMLButtonElement | null>
                );
              }}
              className="w-full"
            >
              <TelephoneTextField
                label={mobileLabel}
                id="contact-mobile"
                data-testid="contact-mobile-input"
                width={
                  isTabletDevice
                    ? isUseSimilarWidthForTelAndEmail
                      ? 347
                      : 305
                    : "auto"
                }
                state={invalidPhoneMessage !== "" ? "error" : ""}
                message={invalidPhoneMessage ?? ""}
                onChange={(e) => handleMobileNumberChange(e)}
                onSelect={(option) => handleOptionSelect(option)}
                value={phone}
                dropDownValue={telCountryCode}
                type="tel"
                onBlur={(event) => {
                  if (
                    event.relatedTarget &&
                    event.relatedTarget.id === "actionButton2"
                  ) {
                    handleContinue();
                  } else {
                    handleBlur(
                      isPhoneNumberValid,
                      phone,
                      setInvalidPhoneMessage
                    );
                  }
                }}
                dropdownOptions={dropDownListToDisplay.map((option) => ({
                  ...option,
                  optDropdownFlag: (
                    <Image
                      src={option.optDropdownFlag.src as string}
                      alt={option.optDropdownFlag.alt}
                      width={24}
                      height={24}
                    />
                  ),
                }))}
                brand="staysure"
              />
            </button>
          )}
        </button>
      </div>
      <button
        type="submit"
        className="absolute right-0 focus:outline-none w-0 h-0 overflow-hidden md:hidden"
        onClick={(e) => {
          e.preventDefault();
          handleContinue();
        }}
      >
        <span className="hidden">_</span>
      </button>
    </>
  );
}
export default ContactNumberSection;
